import { createSlice } from "@reduxjs/toolkit";
import { GetAllLoansList, GetEmployeeLoansList, GetPendingLoansList, addNewLoan, updateExistingLoan, deleteExistingLoan, approveLoan, rejectLoan, recordLoanPayment, pauseLoanMonth, resumeLoanMonth } from "./thunk";

const initialState = {
    loansList: [] as any[],
    employeeLoansList: [] as any[],
    pendingLoansList: [] as any[],
    loading: false,
    error: null as string | null
};

const loansSlice = createSlice({
    name: "loans",
    initialState,
    reducers: {
        clearLoans: (state) => {
            state.loansList = [];
            state.employeeLoansList = [];
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Get All Loans
        builder.addCase(GetAllLoansList.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(GetAllLoansList.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                state.loansList = action.payload.data;
            }
        });
        builder.addCase(GetAllLoansList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Get Employee Loans
        builder.addCase(GetEmployeeLoansList.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(GetEmployeeLoansList.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                state.employeeLoansList = action.payload.data;
            }
        });
        builder.addCase(GetEmployeeLoansList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Get Pending Loans
        builder.addCase(GetPendingLoansList.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(GetPendingLoansList.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                state.pendingLoansList = action.payload.data;
            }
        });
        builder.addCase(GetPendingLoansList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Add New Loan
        builder.addCase(addNewLoan.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(addNewLoan.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                // Add to employee loans list
                state.employeeLoansList.push(action.payload.data);
            }
        });
        builder.addCase(addNewLoan.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Update Loan
        builder.addCase(updateExistingLoan.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateExistingLoan.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                const index = state.employeeLoansList.findIndex((loan: any) => loan.id === action.payload.data.id);
                if (index !== -1) {
                    state.employeeLoansList[index] = action.payload.data;
                }
            }
        });
        builder.addCase(updateExistingLoan.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Delete Loan
        builder.addCase(deleteExistingLoan.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(deleteExistingLoan.fulfilled, (state, action) => {
            state.loading = false;
            // Remove from employee loans list
            state.employeeLoansList = state.employeeLoansList.filter((loan: any) => loan.id !== action.meta.arg.id);
        });
        builder.addCase(deleteExistingLoan.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Approve Loan
        builder.addCase(approveLoan.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(approveLoan.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                const index = state.employeeLoansList.findIndex((loan: any) => loan.id === action.payload.data.id);
                if (index !== -1) {
                    state.employeeLoansList[index] = action.payload.data;
                }
            }
        });
        builder.addCase(approveLoan.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Reject Loan
        builder.addCase(rejectLoan.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(rejectLoan.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                const index = state.employeeLoansList.findIndex((loan: any) => loan.id === action.payload.data.id);
                if (index !== -1) {
                    state.employeeLoansList[index] = action.payload.data;
                }
            }
        });
        builder.addCase(rejectLoan.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Record Payment
        builder.addCase(recordLoanPayment.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(recordLoanPayment.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload?.data) {
                const index = state.employeeLoansList.findIndex((loan: any) => loan.id === action.payload.data.id);
                if (index !== -1) {
                    state.employeeLoansList[index] = action.payload.data;
                }
            }
        });
        builder.addCase(recordLoanPayment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'An error occurred';
        });

        // Pause Loan Month
        builder.addCase(pauseLoanMonth.fulfilled, (state, action) => {
            if (action.payload?.data) {
                const index = state.employeeLoansList.findIndex((loan: any) => loan.id === action.payload.data.id);
                if (index !== -1) state.employeeLoansList[index] = action.payload.data;
            }
        });

        // Resume Loan Month
        builder.addCase(resumeLoanMonth.fulfilled, (state, action) => {
            if (action.payload?.data) {
                const index = state.employeeLoansList.findIndex((loan: any) => loan.id === action.payload.data.id);
                if (index !== -1) state.employeeLoansList[index] = action.payload.data;
            }
        });
    }
});

export const { clearLoans } = loansSlice.actions;
export default loansSlice.reducer; 